import { Component, computed, inject, signal } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ApiService } from '../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';
import { TAGPermission } from '../../../models/tagpermission';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import { TAGUser } from '../../../models/taguser';
import { FieldsetModule } from 'primeng/fieldset';
import { PasswordModule } from 'primeng/password';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { sha256 } from 'js-sha256';

export interface UserEditDialogData {
  user: TAGUser;
}

@Component({
  selector: 'app-internal-requests-edit-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
    FieldsetModule,
    PasswordModule,
    InputNumberModule,
    InputGroupModule,
    MatIcon,
    ToggleButtonModule,
    CheckboxModule,
    FormsModule,
    TooltipModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './userEditDialog.html',
  styleUrl: './userEditDialog.scss',
})
export class UserEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private api = inject(ApiService);

  pinPermission = TAGPermission.pin_login;

  userForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    firstname: new FormControl('', [Validators.required]),
    lastname: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.email]),
    password: new FormControl(''),
    userpin: new FormControl({ value: '', disabled: true }),

    schoolclass: new FormControl(''),
    active: new FormControl(false),
    hidden: new FormControl(false),

    technikEmail: new FormControl('', [Validators.email]),
    technikEmailPassword: new FormControl(''),
  });

  user: TAGUser;
  userPermissions = signal<Set<TAGPermission>>(new Set());
  allGroupPerms = computed(() => {
    let result = [];
    for (let group of this.getAllPermissionGroups()) {
      var hasAll = true;

      for (let p of this.getPermissionsByGroup(group)) {
        if (!this.userPermissions().has(p)) hasAll = false;
      }

      if (hasAll) {
        result.push(group);
      }
    }
    return result;
  });

  constructor(
    public dialogRef: DynamicDialogRef<UserEditDialog>,
    public config: DynamicDialogConfig<UserEditDialogData>,
  ) {
    if (config.data?.user) {
      this.user = new TAGUser(Object.assign({}, config.data.user.serialize()));
      this.userForm.patchValue(this.user);
      this.userPermissions.set(new Set(this.user.permissions));
    } else {
      this.user = new TAGUser();
      this.userForm.reset();
    }

    if (this.user._id) {
      config.header = this.translate.translate('internal.users.editTitle', this.user)();
    } else {
      config.header = this.translate.translate('internal.users.editTitleNew')();
    }
  }

  getAllPermissions(): TAGPermission[] {
    return TAGPermission.allPermissions;
  }

  getAllPermissionGroups(): string[] {
    var groups = new Set<string>();
    for (let p of this.getAllPermissions()) {
      groups.add(p.category);
    }
    return Array.from(groups);
  }

  getPermissionsByGroup(group: string): TAGPermission[] {
    var perms: TAGPermission[] = [];
    for (let p of this.getAllPermissions()) {
      if (p.category == group) perms.push(p);
    }
    return perms;
  }

  hasPerm(permission: string): boolean {
    return this.api.user().hasPermission(TAGPermission.fromName(permission));
  }

  hasAnyGroupPerm(group: string) {
    var hasAny = false;
    for (let p of this.getPermissionsByGroup(group)) {
      if (this.hasPerm(p.name)) hasAny = true;
    }
    return hasAny;
  }

  generateRandomPin() {
    let userpin = '';
    for (var i = 0; i < 4; i++) {
      userpin += Math.floor(Math.random() * 10);
    }

    let charCount: { [key: string]: number } = {};
    for (var i = 0; i < userpin.length; i++) {
      var char = userpin.charAt(i);
      if (charCount[char] == undefined) charCount[char] = 1;
      else charCount[char]++;
    }

    if (Object.values(charCount).filter((v) => v > 2).length > 0) {
      this.generateRandomPin();
    }

    this.userForm.patchValue({ userpin: userpin });
  }

  editPermission(permission: TAGPermission, add: boolean) {
    this.userPermissions.update((p) => {
      if (add) {
        p.add(permission);
      } else {
        p.delete(permission);
      }

      return new Set(p);
    });
  }

  editPermGroup(group: string, add: boolean) {
    this.userPermissions.update((p) => {
      for (let perm of this.getPermissionsByGroup(group)) {
        if (add) {
          p.add(perm);
        } else {
          p.delete(perm);
        }
      }

      return new Set(p);
    });
  }

  deleteUser() {
    if (this.user._id === undefined) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.users.deleteTitle', this.user)(),
        this.translate.translate('internal.users.deleteMessage', this.user)(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.users.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteUser(this.user._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  closeDialog(save?: boolean) {
    if (!save) {
      this.dialogRef.close(false);
      return;
    }

    if (!this.userForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(
        ProgressDialogStepState.WAITING,
        this.translate.translate('internal.users.updateProgress')(),
      ),
    );

    if (this.user._id === undefined) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.users.createProgress')(),
        ),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      this.user.deserialize(this.userForm.value);

      if (!this.user.password) this.user.password = undefined;
      else this.user.password = sha256(this.user.password);

      if (!this.user.technikEmailPassword) this.user.technikEmailPassword = undefined;

      if (this.userForm.controls.userpin.value !== '')
        this.user.pin = this.userForm.controls.userpin.value;

      this.user.permissions = this.userPermissions();

      console.log(this.userForm, this.user);

      if (this.user._id === undefined) {
        this.api
          .newUser(this.user)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updateUser(this.user)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }
}
